import React, { useState, useEffect } from 'react';
import { X, Printer, Download, Check, FileText, Tag, Receipt } from 'lucide-react';
import { CompanyInfo, PrintMode, ServiceTicket } from '../types';
import { calculateTicketTotal, formatDateTR, formatTL, formatPhoneDisplay, STATUS_LABELS, getTicketPaymentSplit, formatTicketPaymentMethodDisplay, getTicketContractBreakdown } from '../utils/storage';
import {
  exportTicketIntakePDF,
  exportTicketInvoicePDF,
  printTicketIntakePDF,
  printTicketInvoicePDF,
} from '../utils/pdfGenerator';
import { LabelDesigner } from './LabelDesigner';
import { PatternThumbnail } from './PatternLockModal';

interface PrintTicketModalProps {
  ticket: ServiceTicket | null;
  mode: PrintMode;
  isOpen: boolean;
  onClose: () => void;
  companyInfo: CompanyInfo;
}

export const PrintTicketModal: React.FC<PrintTicketModalProps> = (props) => {
  if (!props.isOpen || !props.ticket) return null;
  return <PrintTicketModalContent {...props} ticket={props.ticket} />;
};

const PrintTicketModalContent: React.FC<PrintTicketModalProps & { ticket: ServiceTicket }> = ({
  ticket,
  mode,
  isOpen,
  onClose,
  companyInfo,
}) => {
  const [activeTab, setActiveTab] = useState<PrintMode>(mode);
  const [downloaded, setDownloaded] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Sync activeTab when modal is opened with a specific mode
  useEffect(() => {
    if (isOpen && mode) {
      setActiveTab(mode);
    }
  }, [isOpen, mode]);

  const { partsTotal, vatAmount, grandTotal } = calculateTicketTotal(ticket.parts, ticket.laborCost, ticket.vatRate, ticket.discount, ticket.isVatIncluded, ticket.roundingAdjustment || 0);

  const handlePrint = () => {
    try {
      setPrinting(true);
      if (activeTab === 'intake') {
        printTicketIntakePDF(ticket, companyInfo);
      } else if (activeTab === 'invoice') {
        printTicketInvoicePDF(ticket, companyInfo);
      }
      setTimeout(() => setPrinting(false), 2000);
    } catch (err) {
      console.error('PDF print error, falling back to browser print:', err);
      window.print();
      setPrinting(false);
    }
  };

  const handlePDFExport = () => {
    try {
      if (activeTab === 'intake') {
        exportTicketIntakePDF(ticket, companyInfo);
      } else if (activeTab === 'invoice') {
        exportTicketInvoicePDF(ticket, companyInfo);
      }
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2500);
    } catch (err) {
      console.error('PDF export error:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/80 backdrop-blur-sm print:p-0 print:bg-white print:static">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center print:p-0">
        <div className={`bg-white rounded-2xl shadow-2xl w-full ${activeTab === 'label' ? 'max-w-6xl' : 'max-w-4xl'} border border-slate-200 overflow-hidden my-auto text-left print:shadow-none print:border-none print:w-full print:max-w-none transition-all`}>
        {/* Modal Top Control Bar with Mode Tabs */}
        <div className="bg-slate-900 text-white px-5 py-3 flex flex-wrap items-center justify-between no-print print:hidden border-b border-slate-800 gap-3">
          {/* Left: View Tabs */}
          <div className="flex items-center bg-slate-800/90 p-1 rounded-xl border border-slate-700/80">
            <button
              type="button"
              onClick={() => setActiveTab('intake')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'intake'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Kabul Formu</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('label')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'label'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <Tag className="w-3.5 h-3.5 text-amber-400" />
              <span>Cihaz Etiketi Bas</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('invoice')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'invoice'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <Receipt className="w-3.5 h-3.5" />
              <span>Fatura / Makbuz</span>
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2">
            {activeTab !== 'label' && (
              <>
                <button
                  onClick={handlePrint}
                  disabled={printing}
                  title="Doğrudan Temiz PDF Olarak Yazdır"
                  className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg flex items-center space-x-1.5 shadow-sm transition-colors cursor-pointer disabled:opacity-75"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>{printing ? 'Yazdırılıyor...' : 'Yazdır (PDF)'}</span>
                </button>

                <button
                  onClick={handlePDFExport}
                  className={`px-3.5 py-1.5 text-white text-xs font-bold rounded-lg flex items-center space-x-1.5 shadow-sm transition-colors cursor-pointer ${
                    downloaded
                      ? 'bg-emerald-600 hover:bg-emerald-500 ring-2 ring-emerald-300'
                      : 'bg-emerald-700 hover:bg-emerald-600'
                  }`}
                >
                  {downloaded ? (
                    <Check className="w-3.5 h-3.5 text-white" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  <span>{downloaded ? 'İndirildi ✓' : 'PDF İndir'}</span>
                </button>
              </>
            )}

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* MODAL CONTENT: LABEL DESIGNER OR A4 DOCUMENT */}
        {activeTab === 'label' ? (
          <LabelDesigner
            ticket={ticket}
            companyInfo={companyInfo}
            onPrintIntakeMode={() => setActiveTab('intake')}
          />
        ) : (
          /* PRINTABLE DOCUMENT BODY (A4 Intake or Invoice) */
          <div
            id="printable-document"
            className="p-8 text-slate-800 font-sans print:p-0 print:text-black bg-white flex flex-col justify-between min-h-[720px] print:min-h-[260mm] space-y-4 max-h-[80vh] overflow-y-auto"
          >
            {/* Clean Modern Header (No black stripe) */}
            <div className="flex items-start justify-between pb-3 border-b border-slate-200 gap-4">
              <div className="flex items-start gap-3.5">
                {companyInfo.logoUrl && (
                  <div className="shrink-0 pt-0.5">
                    <img
                      src={companyInfo.logoUrl}
                      alt={companyInfo.name}
                      className="max-h-16 max-w-[130px] object-contain rounded"
                    />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900 uppercase">
                    {companyInfo.name}
                  </h1>
                  <p className="text-xs md:text-sm text-slate-600 mt-1">
                    {companyInfo.address}
                  </p>
                  <p className="text-xs md:text-sm text-slate-600 mt-0.5 font-medium">
                    Tel: <span className="font-bold text-slate-800">{companyInfo.phone}</span>
                    {companyInfo.email ? (
                      <span>
                        {' '}• E-Posta: <span className="font-bold text-slate-800">{companyInfo.email}</span>
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>

              <div className="text-right shrink-0">
                <div className="bg-slate-50 border border-slate-200 px-4 py-2 rounded-xl inline-block text-center shadow-xs">
                  <span className="block text-base md:text-lg font-mono font-black text-blue-700">
                    {ticket.id}
                  </span>
                  <span className="text-xs md:text-sm font-semibold text-slate-600">
                    {activeTab === 'intake'
                      ? `Tarih: ${formatDateTR(ticket.createdAt)}`
                      : `Tarih: ${ticket.deliveryDate ? formatDateTR(ticket.deliveryDate) : formatDateTR(ticket.updatedAt)}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-center bg-slate-50/90 py-2.5 rounded-xl font-black text-sm md:text-base tracking-wider uppercase border border-slate-200 text-slate-900 shadow-xs">
              {activeTab === 'intake'
                ? 'MÜŞTERİ CİHAZ TESLİM VE KABUL TUTANAĞI'
                : 'SERVİS İŞLEM FATURASI VE TESLİMAT MAKBUZU'}
            </div>

            {/* Section 1: Customer Info & Device Info Blocks */}
            <div className="grid grid-cols-2 gap-4 text-xs md:text-sm">
              <div className="bg-slate-50/90 p-4 rounded-xl border border-slate-200">
                <span className="font-bold text-slate-500 block mb-1 text-xs uppercase tracking-wider">MÜŞTERİ BİLGİLERİ</span>
                <p className="font-black text-slate-900 text-base">{ticket.customerName}</p>
                <p className="text-slate-800 mt-1 font-medium">Tel: <span className="font-bold text-slate-900 font-mono text-sm md:text-base">{formatPhoneDisplay(ticket.customerPhone)}</span></p>
                {ticket.customerEmail && <p className="text-slate-700 mt-0.5 font-medium">E-Posta: {ticket.customerEmail}</p>}
                {ticket.customerAddress && <p className="text-slate-700 mt-0.5 font-medium">Adres: {ticket.customerAddress}</p>}
              </div>

              <div className="bg-slate-50/90 p-4 rounded-xl border border-slate-200">
                <span className="font-bold text-slate-500 block mb-1 text-xs uppercase tracking-wider">CİHAZ BİLGİLERİ</span>
                <p className="font-black text-slate-900 text-base">
                  {ticket.deviceType} • {ticket.deviceBrand} {ticket.deviceModel}
                </p>
                <p className="text-slate-800 mt-1 font-mono font-medium">Seri No: <span className="font-bold">{ticket.serialNumber || 'Belirtilmedi'}</span></p>
                <p className="font-bold text-slate-800 mt-1 flex items-center gap-1">
                  Garanti Durumu: <span className={ticket.isUnderWarranty ? 'text-emerald-700 font-black' : 'text-slate-700 font-black'}>{ticket.isUnderWarranty ? 'GARANTİ KAPSAMINDA' : 'GARANTİ DIŞI'}</span>
                </p>
              </div>
            </div>

            {activeTab === 'intake' ? (
              /* INTAKE FORM SPECIFIC BODY */
              <div className="flex flex-col justify-between flex-1 space-y-4">
                {/* Detailed Device Condition */}
                <div className="space-y-2 text-xs md:text-sm">
                  <h4 className="font-bold text-slate-900 border-b pb-1 border-slate-200">
                    CİHAZ DETAYLI DURUMU & TESLİM ALINAN AKSESUARLAR:
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-lg border border-slate-200">
                    <div>
                      <span className="text-slate-500 block font-semibold text-xs mb-0.5">Fiziksel Durum Notu:</span>
                      <p className="text-slate-900 font-medium">{ticket.physicalCondition || 'Gözle görülür hasar yok.'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 block font-semibold text-xs mb-0.5">Teslim Alınan Aksesuarlar:</span>
                      <p className="text-slate-900 font-medium">
                        {ticket.includedAccessories.length > 0
                          ? ticket.includedAccessories.join(', ')
                          : 'Yalnız cihaz teslim alındı.'}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500 block font-semibold text-xs mb-0.5">Ekran Kilit / PIN:</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-slate-900 font-bold">{ticket.devicePassword || 'Yok / Belirtilmedi'}</p>
                        {ticket.devicePassword && (
                          <PatternThumbnail patternStr={ticket.devicePassword} size={32} className="shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Problem Description */}
                <div className="space-y-1.5 text-xs md:text-sm">
                  <h4 className="font-bold text-slate-900 border-b pb-1 border-slate-200">
                    ARIZA VE MÜŞTERİ ŞİKAYETİ:
                  </h4>
                  <p className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 leading-relaxed font-medium text-slate-800 text-sm">
                    {ticket.issueDescription}
                  </p>
                </div>

                {/* Paid Intake / Deposit Fee Banner */}
                {((ticket.paidAmount && ticket.paidAmount > 0) || (ticket.payments && ticket.payments.length > 0)) && (
                  <div className="bg-emerald-50/95 p-3 rounded-xl border border-emerald-300 text-xs md:text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-2xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-emerald-950 flex items-center gap-1.5">
                        <span className="text-lg">💵</span>
                        <span>Tahsil Edilen Giriş Ücreti / Ön Ödeme:</span>
                      </span>
                      <span className="font-mono font-black text-emerald-900 text-base md:text-lg">
                        {formatTL(ticket.paidAmount || (ticket.payments && ticket.payments.reduce((s, p) => s + (p.amount || 0), 0)) || 0)}
                      </span>
                      <span className="text-xs bg-emerald-100 text-emerald-900 font-bold px-2 py-0.5 rounded-md border border-emerald-300">
                        {formatTicketPaymentMethodDisplay(ticket)}
                      </span>
                    </div>
                    <span className="text-xs text-emerald-900 font-bold bg-white/90 px-2.5 py-1 rounded border border-emerald-300">
                      ✓ Tahsil Edildi (Makbuz Kesildi)
                    </span>
                  </div>
                )}

                {/* Service Terms */}
                <div className="space-y-1 text-xs text-slate-600">
                  <h4 className="font-bold text-slate-800 border-b pb-1 border-slate-200 text-xs md:text-sm">
                    SERVİS KABUL VE TESLİMAT ŞARTLARI:
                  </h4>
                  <p className="whitespace-pre-line leading-relaxed bg-slate-50 p-3 rounded border border-slate-200 font-medium">
                    {companyInfo.terms}
                  </p>
                </div>

                {/* Signatures */}
                <div className="pt-6 grid grid-cols-2 gap-12 text-center text-xs md:text-sm mt-auto">
                  <div>
                    <div className="border-b border-slate-400 h-10 mb-2" />
                    <p className="font-bold text-slate-900 text-sm">MÜŞTERİ İMZASI</p>
                    <p className="text-sm font-bold text-slate-900 my-0.5">{ticket.customerName}</p>
                    <p className="text-xs text-slate-500">Cihazımı yukarıda belirtilen durumda teslim ettim.</p>
                  </div>
                  <div>
                    <div className="border-b border-slate-400 h-10 mb-2" />
                    <p className="font-bold text-slate-900 text-sm">TESLİM ALAN SERVİS YETKİLİSİ</p>
                    <p className="text-sm font-bold text-slate-900 my-0.5">
                      {ticket.createdByName || ticket.createdByUser || companyInfo.name}
                    </p>
                    <p className="text-xs text-slate-500">{companyInfo.name} • Kaşe / İmza</p>
                  </div>
                </div>

                {/* Online Tracking Note at Form Bottom */}
                <div className="bg-blue-50/90 p-3 rounded-lg border border-blue-200 text-xs md:text-sm mt-3 space-y-1 text-center sm:text-left">
                  <div className="flex items-center gap-2 text-blue-900 flex-wrap justify-center sm:justify-start">
                    <span className="font-bold text-blue-800 flex items-center gap-1">
                      <span>📱</span>
                    </span>
                    <span className="font-mono font-bold text-blue-950 bg-white px-2.5 py-0.5 rounded border border-blue-300 text-xs md:text-sm shadow-xs break-all">
                      teknikservx.com/takip
                    </span>
                  </div>
                  <div className="text-xs md:text-sm text-slate-700 font-medium">
                    Cihazınızın durumunu form numarası (<strong className="font-mono font-bold text-blue-950">{ticket.id}</strong>) ve telefon numaranız (<strong className="font-mono font-bold text-blue-950">{formatPhoneDisplay(ticket.customerPhone)}</strong>) ile web sitemizden sorgulayabilirsiniz.
                  </div>
                </div>
              </div>
            ) : (
              /* INVOICE SPECIFIC BODY */
              <div className="flex flex-col justify-between flex-1 space-y-4">
                {/* Itemized Parts & Services Table */}
                <div className="space-y-2">
                  <h4 className="font-bold text-xs md:text-sm text-slate-900 border-b pb-1 border-slate-200">
                    YAPILAN İŞLEMLER VE DEĞİŞEN PARÇALAR:
                  </h4>

                  <table className="w-full text-left border-collapse text-xs md:text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-y border-slate-300 font-bold text-slate-800">
                        <th className="py-2.5 px-3">#</th>
                        <th className="py-2.5 px-3">Açıklama / Parça Adı</th>
                        <th className="py-2.5 px-3 text-center">Adet</th>
                        <th className="py-2.5 px-3 text-right">Birim Fiyat</th>
                        <th className="py-2.5 px-3 text-right">Toplam</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {ticket.parts.map((p, idx) => (
                        <tr key={p.id}>
                          <td className="py-2 px-3">{idx + 1}</td>
                          <td className="py-2 px-3 font-semibold text-slate-800">Yedek Parça: {p.name}</td>
                          <td className="py-2 px-3 text-center font-bold">{p.quantity}</td>
                          <td className="py-2 px-3 text-right">{formatTL(p.unitPrice)}</td>
                          <td className="py-2 px-3 text-right font-bold text-slate-900">{formatTL(p.quantity * p.unitPrice)}</td>
                        </tr>
                      ))}

                      {ticket.laborCost > 0 && (
                        <tr>
                          <td className="py-2 px-3">{ticket.parts.length + 1}</td>
                          <td className="py-2 px-3 font-semibold text-slate-800">İşçilik</td>
                          <td className="py-2 px-3 text-center font-bold">1</td>
                          <td className="py-2 px-3 text-right">{formatTL(ticket.laborCost)}</td>
                          <td className="py-2 px-3 text-right font-bold text-slate-900">{formatTL(ticket.laborCost)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Financial Totals */}
                {(() => {
                  const isContract = ticket.serviceType === 'Sozlesmeli' || Boolean(ticket.contractedCompanyId);
                  const contractBreakdown = getTicketContractBreakdown(ticket);
                  const split = getTicketPaymentSplit(ticket);
                  const totalPaid = split.total;
                  const remainingDebt = isContract ? contractBreakdown.effectiveDebt : Math.max(0, grandTotal - totalPaid);

                  let paymentStatusText = 'Ödenmedi';
                  if (isContract) {
                    if (!contractBreakdown.hasOutOfContractParts) {
                      paymentStatusText = 'Sözleşme Kapsamında (Ödendi)';
                    } else if (remainingDebt <= 0) {
                      paymentStatusText = 'Harici Parça Tahsil Edildi (Tam Ödendi)';
                    } else if (totalPaid > 0) {
                      paymentStatusText = 'Kısmi Harici Ödendi';
                    } else {
                      paymentStatusText = 'Harici Ödeme Bekliyor';
                    }
                  } else {
                    if (ticket.paymentStatus === 'KismiOdendi' || (totalPaid > 0 && remainingDebt > 0)) {
                      paymentStatusText = 'Kısmi Ödendi';
                    } else if (ticket.paymentStatus === 'Odendi' || (totalPaid >= grandTotal && grandTotal > 0)) {
                      paymentStatusText = 'Tam Ödendi';
                    } else {
                      paymentStatusText = 'Ödenmedi';
                    }
                  }

                  return (
                    <div className="flex justify-between items-start pt-2 border-t border-slate-200 text-xs md:text-sm">
                      <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 w-1/2 space-y-1.5">
                        <p className="font-bold text-slate-900">ÖDEME & TAHSİLAT DETAYI:</p>
                        <p className="text-slate-700">
                          Ödeme Durumu:{' '}
                          <span className="font-black uppercase text-slate-900">
                            {paymentStatusText}
                          </span>
                        </p>

                        <div className="space-y-1 bg-white p-2.5 rounded border border-slate-200">
                          <p className="text-slate-800 flex justify-between font-semibold">
                            <span>Toplam Alınan:</span>
                            <span className="font-bold text-emerald-700 font-mono text-sm">{formatTL(totalPaid)}</span>
                          </p>
                          {split.cash > 0 && (
                            <p className="text-xs text-slate-600 flex justify-between pl-2">
                              <span>• Nakit:</span>
                              <span className="font-mono font-medium">{formatTL(split.cash)}</span>
                            </p>
                          )}
                          {split.card > 0 && (
                            <p className="text-xs text-slate-600 flex justify-between pl-2">
                              <span>• Kredi Kartı / POS:</span>
                              <span className="font-mono font-medium">{formatTL(split.card)}</span>
                            </p>
                          )}
                          {split.transfer > 0 && (
                            <p className="text-xs text-slate-600 flex justify-between pl-2">
                              <span>• Havale / EFT:</span>
                              <span className="font-mono font-medium">{formatTL(split.transfer)}</span>
                            </p>
                          )}
                          {remainingDebt > 0 && (
                            <p className="text-slate-900 flex justify-between border-t border-slate-100 pt-1 font-bold">
                              <span>Kalan Borç / Bakiye:</span>
                              <span className="font-black text-rose-700 font-mono text-sm">{formatTL(remainingDebt)}</span>
                            </p>
                          )}
                        </div>

                        <p className="text-slate-700 font-medium">
                          Ödeme Yöntemi: <span className="font-bold text-slate-900">{formatTicketPaymentMethodDisplay(ticket)}</span>
                        </p>
                        <p className="text-slate-700 font-medium">
                          Servis Durumu: <span className="font-bold text-slate-900">{STATUS_LABELS[ticket.status]}</span>
                        </p>
                      </div>

                      <div className="w-1/3 space-y-2 text-right font-medium text-xs md:text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Yedek Parça Toplamı:</span>
                          <span className="font-mono font-bold text-slate-800">{formatTL(partsTotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">İşçilik:</span>
                          <span className="font-mono font-bold text-slate-800">{formatTL(ticket.laborCost)}</span>
                        </div>
                        {ticket.discount > 0 && (
                          <div className="flex justify-between text-rose-600 font-bold">
                            <span>İskonto / İndirim:</span>
                            <span className="font-mono">-{formatTL(ticket.discount)}</span>
                          </div>
                        )}
                        {(ticket.roundingAdjustment || 0) !== 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Yuvarlama:</span>
                            <span className="font-mono">{(ticket.roundingAdjustment || 0) > 0 ? '+' : ''}{formatTL(ticket.roundingAdjustment || 0)}</span>
                          </div>
                        )}
                        {ticket.vatRate > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">
                              KDV (%20):
                            </span>
                            <span className="font-mono font-bold text-slate-800">{formatTL(vatAmount)}</span>
                          </div>
                        )}
                        <div className="border-t border-slate-900 pt-1.5 flex justify-between font-black text-sm md:text-base text-slate-900">
                          <span>GENEL TOPLAM:</span>
                          <span className="font-mono text-base md:text-lg text-blue-700">{formatTL(grandTotal)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {ticket.performedActions && (
                  <div className="text-xs md:text-sm space-y-1">
                    <span className="font-bold text-slate-900 block">YAPILAN İŞLEMLER / ONARIM DETAYI:</span>
                    <p className="bg-slate-50/70 p-3 rounded-lg border border-slate-200 text-slate-800 font-medium">
                      {ticket.performedActions}
                    </p>
                  </div>
                )}

                <p className="text-xs md:text-sm text-slate-600 font-medium italic text-center pt-2">
                  {(ticket.warrantyDays !== undefined ? ticket.warrantyDays : 30) === 0
                    ? 'Yapılan işlem servis garantisi kapsamı dışındadır.'
                    : `Yapılan işlem teslim tarihinden itibaren ${ticket.warrantyDays !== undefined ? ticket.warrantyDays : 30} gün servis garantimiz altındadır.`}
                </p>

                {/* Delivery Signatures pinned at bottom */}
                <div className="pt-6 grid grid-cols-2 gap-12 text-center text-xs md:text-sm mt-auto">
                  <div>
                    <div className="border-b border-slate-400 h-10 mb-2" />
                    <p className="font-bold text-slate-900 text-sm">TESLİM ALAN MÜŞTERİ</p>
                    <p className="text-sm font-bold text-slate-900 my-0.5">{ticket.customerName}</p>
                    <p className="text-xs text-slate-500">Cihazımı eksiksiz ve çalışır durumda teslim aldım.</p>
                  </div>
                  <div>
                    <div className="border-b border-slate-400 h-10 mb-2" />
                    <p className="font-bold text-slate-900 text-sm">TESLİM EDEN / SERVİS YETKİLİSİ</p>
                    <p className="text-sm font-bold text-slate-900 my-0.5">
                      {ticket.deliveredByName || ticket.repairedByName || ticket.createdByName || ticket.createdByUser || companyInfo.name}
                    </p>
                    <p className="text-xs text-slate-500">{companyInfo.name} • Kaşe / İmza</p>
                  </div>
                </div>

                {/* Online Tracking Note at Form Bottom */}
                
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
